import type { StateCreator } from 'zustand';
import type { StoreState } from './index';
import type { Brand, BrandWithChannels, BrandChannel, BrandCompetitor, BenchmarkMetric } from '../types';
import { brandApi } from '../services/brandApi';

export interface BrandSlice {
  currentBrand: BrandWithChannels | null;
  brandCompetitors: BrandCompetitor[];
  brandBenchmark: BenchmarkMetric[];
  allBrands: Brand[];
  brandLoading: boolean;

  fetchBrand: (brandId: number) => Promise<void>;
  fetchBrands: () => Promise<void>;
  createBrand: (data: Partial<Brand>) => Promise<BrandWithChannels>;
  updateBrand: (brandId: number, data: Partial<Brand>) => Promise<void>;
  deleteBrand: (brandId: number) => Promise<void>;

  addChannel: (brandId: number, data: Partial<BrandChannel>) => Promise<void>;
  updateChannel: (brandId: number, channelId: number, data: Partial<BrandChannel>) => Promise<void>;
  deleteChannel: (brandId: number, channelId: number) => Promise<void>;
  setPrimaryChannel: (brandId: number, channelId: number) => Promise<void>;

  fetchCompetitors: (brandId: number) => Promise<void>;
  addCompetitor: (brandId: number, data: { username?: string; competitor_brand_id?: number; notes?: string }) => Promise<void>;
  removeCompetitor: (brandId: number, competitorBrandId: number) => Promise<void>;

  checkAccount: (brandId: number, username: string) => Promise<{ exists: boolean; account_id: number | null }>;
  scrapeCompetitor: (brandId: number, username: string) => Promise<BrandWithChannels>;

  fetchBenchmark: (brandId: number) => Promise<void>;
}

export const createBrandSlice: StateCreator<StoreState, [], [], BrandSlice> = (set, _get) => ({
  currentBrand: null,
  brandCompetitors: [],
  brandBenchmark: [],
  allBrands: [],
  brandLoading: false,

  fetchBrand: async (brandId) => {
    set({ brandLoading: true });
    try {
      const brand = await brandApi.get(brandId);
      set({ currentBrand: brand, brandLoading: false });
    } catch {
      set({ brandLoading: false });
    }
  },

  fetchBrands: async () => {
    try {
      const brands = await brandApi.list();
      set({ allBrands: brands });
    } catch {
      // ignore
    }
  },

  createBrand: async (data) => {
    const brand = await brandApi.create(data);
    set((s) => ({ allBrands: [...s.allBrands, brand], currentBrand: brand }));
    return brand;
  },

  updateBrand: async (brandId, data) => {
    const updated = await brandApi.update(brandId, data);
    set((s) => ({
      allBrands: s.allBrands.map((b) => (b.id === brandId ? { ...b, ...updated } : b)),
      currentBrand: s.currentBrand?.id === brandId ? { ...s.currentBrand, ...updated } : s.currentBrand,
    }));
  },

  deleteBrand: async (brandId) => {
    await brandApi.delete(brandId);
    set((s) => ({
      allBrands: s.allBrands.filter((b) => b.id !== brandId),
      currentBrand: s.currentBrand?.id === brandId ? null : s.currentBrand,
    }));
  },

  addChannel: async (brandId, data) => {
    await brandApi.addChannel(brandId, data);
    const brand = await brandApi.get(brandId);
    set((s) => ({
      currentBrand: s.currentBrand?.id === brandId ? brand : s.currentBrand,
    }));
  },

  updateChannel: async (brandId, channelId, data) => {
    await brandApi.updateChannel(brandId, channelId, data);
    const brand = await brandApi.get(brandId);
    set((s) => ({
      currentBrand: s.currentBrand?.id === brandId ? brand : s.currentBrand,
    }));
  },

  deleteChannel: async (brandId, channelId) => {
    await brandApi.deleteChannel(brandId, channelId);
    set((s) => ({
      currentBrand: s.currentBrand?.id === brandId
        ? { ...s.currentBrand, channels: s.currentBrand.channels.filter((c) => c.id !== channelId) }
        : s.currentBrand,
    }));
  },

  setPrimaryChannel: async (brandId, channelId) => {
    await brandApi.setPrimaryChannel(brandId, channelId);
    const brand = await brandApi.get(brandId);
    set((s) => ({
      currentBrand: s.currentBrand?.id === brandId ? brand : s.currentBrand,
    }));
  },

  fetchCompetitors: async (brandId) => {
    try {
      const competitors = await brandApi.getCompetitors(brandId);
      set({ brandCompetitors: competitors });
    } catch {
      // ignore
    }
  },

  addCompetitor: async (brandId, data) => {
    await brandApi.addCompetitor(brandId, data);
    const competitors = await brandApi.getCompetitors(brandId);
    set({ brandCompetitors: competitors });
  },

  removeCompetitor: async (brandId, competitorBrandId) => {
    await brandApi.removeCompetitor(brandId, competitorBrandId);
    set((s) => ({
      brandCompetitors: s.brandCompetitors.filter((c) => c.competitor_brand_id !== competitorBrandId),
    }));
  },

  checkAccount: async (brandId, username) => {
    return brandApi.checkAccount(brandId, username);
  },

  scrapeCompetitor: async (brandId, username) => {
    return brandApi.scrapeCompetitor(brandId, username);
  },

  fetchBenchmark: async (brandId) => {
    try {
      const benchmark = await brandApi.getBenchmark(brandId);
      set({ brandBenchmark: benchmark });
    } catch {
      // ignore
    }
  },
});
